/**
 * Maintenance mode: when TruckBuddy is closed on purpose, and who still gets in.
 *
 * Modelled on SigmaBuddy's SIGM-205 (`backend/maintenance_policy.py` and
 * `frontend/src/lib/maintenance.ts` there), adapted to this repo. Everything that
 * DECIDES lives here as pure functions, so the API's gate, the public status
 * route, the splash page and the Settings screen cannot disagree about what state
 * the system is in.
 *
 * ── Three states, not two ───────────────────────────────────────────────
 *
 * `off`        nothing announced; the app behaves normally.
 * `scheduled`  a window is armed and its start is in the future. The app is
 *              FULLY USABLE: this state exists only to put a countdown in front
 *              of people before the doors shut.
 * `active`     the window has started. Everyone who cannot work through it sees
 *              the maintenance page instead of the app.
 *
 * The move from scheduled to active is worked out from the clock on every read,
 * never flipped by a job. A job that sets a flag at T-0 is one more thing that can
 * fail to fire, and when it fails the banner counts down to zero and nothing
 * happens.
 *
 * ── The environment variable wins, in both directions ───────────────────
 *
 * `TB_MAINTENANCE_MODE` is `auto` / `on` / `off` and it beats the database row
 * either way. `on` is for when the database IS the outage (a row nobody can read
 * cannot close the doors). `off` is the anti-lockout lever for when the row is
 * wrong. Anything unrecognised reads as `auto`: a typo in a deploy variable must
 * not decide, in either direction, whether the whole application is reachable.
 *
 * A row that cannot be read resolves to `off`, and says so (`degraded`). Locking
 * every user out of a healthy application because one query failed is the worse
 * of the two mistakes.
 *
 * Browser-safe: types, arithmetic and `Intl`. No storage, no fetch.
 */
import { z } from "zod";
import { strictObject } from "./requests.js";
import { capabilitiesFor } from "./permissions.js";
import type { UserRole } from "./vocabulary.js";

export const siteMaintenanceStates = ["off", "scheduled", "active"] as const;
export type SiteMaintenanceStateName = (typeof siteMaintenanceStates)[number];

/** `TB_MAINTENANCE_MODE`. See the header for why there are three. */
export const siteMaintenanceModes = ["auto", "on", "off"] as const;
export type SiteMaintenanceMode = (typeof siteMaintenanceModes)[number];

/**
 * Where the answer came from. Shown on the Settings screen so an admin whose
 * switch appears to do nothing can see that a deploy-level override is
 * overruling them, rather than concluding the screen is broken.
 */
export type SiteMaintenanceSource = "database" | "environment" | "fallback";

/** The longest message the page will carry. Matches the column's CHECK. */
export const SITE_MAINTENANCE_MESSAGE_MAX = 500;

/** The `maintenance_window` singleton, as the API reads it. */
export interface SiteMaintenanceWindowRow {
  armed: boolean;
  /** ISO instant. */
  startsAt: string | null;
  /** ISO instant, or null for "until we say so". */
  expectedEndAt: string | null;
  message: string;
  /** "Start now": the window is active whatever `startsAt` says. */
  forceActive: boolean;
  /** Reopen by itself once `expectedEndAt` passes. Off unless chosen. */
  autoEnd: boolean;
  version: number;
}

/** The resolved answer, and enough context for a screen to explain itself. */
export interface SiteMaintenanceState {
  state: SiteMaintenanceStateName;
  source: SiteMaintenanceSource;
  startsAt: string | null;
  expectedEndAt: string | null;
  message: string;
  /**
   * Changes whenever the window is re-announced, so a person who dismissed the
   * popup is not shown it again until there is something new to read.
   */
  announcementId: string;
  /**
   * True when the window could not be read. "We are not in maintenance" and "we
   * could not find out" are different claims, and the status route says which.
   */
  degraded: boolean;
}

/**
 * `GET /maintenance-status`, unauthenticated.
 *
 * Deliberately viewer-agnostic: it says what the SYSTEM is doing, never what
 * this caller may do. The browser works out its own bypass from the session it
 * already holds, which keeps an unauthenticated route from being a way to ask
 * "is this cookie an admin's?".
 */
export interface SiteMaintenanceStatus extends SiteMaintenanceState {
  /** The server's clock, so a viewer whose clock is wrong still counts down right. */
  serverTime: string;
  /** The zone every time on the page is written in. */
  fleetTimeZone: string;
  /**
   * What this deployment calls itself beside the brand -- "Testing" on staging
   * -- or null, which is production. From `TB_ENVIRONMENT_LABEL`, which only
   * staging's deploy and staging's infrastructure set, so the same code on
   * `main` shows nothing. Here because SigmaBuddy's maintenance status carries
   * its environment name too, and the browser already polls this on every page.
   */
  environmentLabel: string | null;
}

/** The longest environment label the brand carries. */
export const ENVIRONMENT_LABEL_MAX = 24;

/**
 * `TB_ENVIRONMENT_LABEL`, read strictly: trimmed, and blank means none. A label
 * is display text, so anything longer than the brand can carry is cut rather
 * than refused -- a typo in it must never stop the API booting.
 */
export function normalizeEnvironmentLabel(raw: string | null | undefined): string | null {
  const value = (raw ?? "").trim();
  return value ? value.slice(0, ENVIRONMENT_LABEL_MAX) : null;
}

/** What `GET /api/maintenance` answers: the Settings screen. */
export interface SiteMaintenanceSettings {
  status: SiteMaintenanceStatus;
  window: SiteMaintenanceWindowRow & {
    updatedAt: string | null;
    updatedByLabel: string | null;
  };
  /** `TB_MAINTENANCE_MODE` as this server read it at boot. */
  environmentMode: SiteMaintenanceMode;
  /**
   * Active people whose OWN roles let them work through a window. Counted live,
   * because "who can still get in" is exactly what an admin must check before
   * arming one.
   */
  bypassHolders: number;
}

/** Read `TB_MAINTENANCE_MODE`. Anything unrecognised means `auto`. */
export function normalizeSiteMaintenanceMode(raw: string | null | undefined): SiteMaintenanceMode {
  const value = (raw ?? "").trim().toLowerCase();
  return (siteMaintenanceModes as readonly string[]).includes(value)
    ? (value as SiteMaintenanceMode)
    : "auto";
}

function epochSeconds(iso: string | null): number {
  if (!iso) return 0;
  const ms = new Date(iso).getTime();
  return Number.isNaN(ms) ? 0 : Math.floor(ms / 1000);
}

/**
 * Identifies one announcement. The version alone would re-prompt on a save that
 * changed nothing a reader cares about; the start alone would not change when the
 * message was rewritten. Together they change when there is something new.
 */
export function siteMaintenanceAnnouncementId(version: number, startsAt: string | null): string {
  return `${version}:${epochSeconds(startsAt)}`;
}

/**
 * The single decision. The gate, the status route and the Settings screen all go
 * through here.
 *
 * `now` is passed in rather than read so the boundary between scheduled and
 * active can be tested exactly.
 */
export function resolveSiteMaintenanceState(input: {
  mode: SiteMaintenanceMode;
  row: SiteMaintenanceWindowRow | null;
  now: Date;
}): SiteMaintenanceState {
  const { mode, row, now } = input;
  const empty = {
    startsAt: null,
    expectedEndAt: null,
    message: "",
    announcementId: "",
    degraded: false,
  };

  if (mode === "off") {
    // Nothing in the database can override this, which is the point: if it
    // could, it would be useless in exactly the situation it exists for.
    return { ...empty, state: "off", source: "environment" };
  }

  if (mode === "on") {
    return {
      ...empty,
      state: "active",
      source: "environment",
      expectedEndAt: row?.expectedEndAt ?? null,
      message: row?.message ?? "",
      announcementId: "env",
    };
  }

  if (!row) {
    return { ...empty, state: "off", source: "fallback", degraded: true };
  }

  const base = {
    source: "database" as const,
    startsAt: row.startsAt,
    expectedEndAt: row.expectedEndAt,
    message: row.message,
    announcementId: siteMaintenanceAnnouncementId(row.version, row.startsAt),
    degraded: false,
  };

  // Unarmed says nothing more: the row keeps the last window's message and
  // times for the next one, and the public status must not echo them.
  if (!row.armed) return { ...empty, state: "off", source: "database" };

  const nowMs = now.getTime();
  const endMs = row.expectedEndAt ? new Date(row.expectedEndAt).getTime() : Number.NaN;
  // Opt-in self-clearing. Off by default so a window cannot reopen the app while
  // somebody is still mid-deploy.
  if (row.autoEnd && !Number.isNaN(endMs) && nowMs > endMs) {
    return { ...base, state: "off" };
  }

  const startMs = row.startsAt ? new Date(row.startsAt).getTime() : Number.NaN;
  // "Start now" is kept apart from `startsAt` so the announced time stays on the
  // page rather than being rewritten to the moment somebody pressed the button.
  if (row.forceActive || Number.isNaN(startMs) || nowMs >= startMs) {
    return { ...base, state: "active" };
  }
  return { ...base, state: "scheduled" };
}

/** A payload that really is a maintenance status. Defensive: it crosses the wire. */
export function isSiteMaintenanceStatus(value: unknown): value is SiteMaintenanceStatus {
  if (!value || typeof value !== "object") return false;
  const state = (value as { state?: unknown }).state;
  return typeof state === "string" && (siteMaintenanceStates as readonly string[]).includes(state);
}

/* ── who works through a window ─────────────────────────────────────── */

/**
 * Whether an account may work through an ACTIVE window.
 *
 * Read from the account's REAL roles, never the previewed ones (TRBUD-253). A
 * preview narrows what you may do with the data; it is not a reason to be locked
 * out of the building. An admin previewing a mechanic during a window would
 * otherwise be refused the very request that exits the preview.
 */
export function canBypassSiteMaintenance(realRoles: readonly UserRole[]): boolean {
  return realRoles.some((role) => capabilitiesFor(role).bypassSiteMaintenance);
}

/** What the shell renders for this viewer. */
export type SiteMaintenanceView = "app" | "locked" | "app-with-bypass-warning";

export function siteMaintenanceView(
  status: Pick<SiteMaintenanceStatus, "state"> | null | undefined,
  canBypass: boolean,
): SiteMaintenanceView {
  if (!status || status.state !== "active") return "app";
  if (!canBypass) return "locked";
  // Somebody smoke-testing a "working" app has no way to know everyone else is
  // locked out unless the app says so, every time.
  return "app-with-bypass-warning";
}

/* ── time ───────────────────────────────────────────────────────────── */

const partsFormatters = new Map<string, Intl.DateTimeFormat>();
function partsFormatter(timeZone: string): Intl.DateTimeFormat {
  let formatter = partsFormatters.get(timeZone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
    partsFormatters.set(timeZone, formatter);
  }
  return formatter;
}

/** The wall clock in `timeZone` at `instant`, written as if it were UTC. */
function wallAsUtcMs(instant: Date, timeZone: string): number {
  const parts = partsFormatter(timeZone).formatToParts(instant);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value);
  // Some engines render midnight as hour 24 even under h23.
  const hour = get("hour") === 24 ? 0 : get("hour");
  return Date.UTC(get("year"), get("month") - 1, get("day"), hour, get("minute"), get("second"));
}

/**
 * A `datetime-local` value, read as wall-clock time in `timeZone`, as an ISO
 * instant. Null when the value is not a real time.
 *
 * In the FLEET zone, not the browser's. The picker is labelled with the fleet
 * zone, and a browser on a laptop still set to Pacific would otherwise arm a
 * window two hours late with nothing looking wrong until the doors shut.
 *
 * Two passes because the offset depends on the instant being solved for, then a
 * gap correction: on the spring-forward morning 02:30 does not exist, and the
 * solve lands an hour EARLY. For a maintenance window that is the wrong direction
 * to be wrong in, so it is nudged forward past the gap.
 */
export function zoneInputToInstant(input: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec((input || "").trim());
  if (!match) return null;
  const [year, month, day, hour, minute] = match.slice(1).map(Number) as [
    number,
    number,
    number,
    number,
    number,
  ];
  // Range-checked rather than left to Date.UTC, which rolls 2026-13-45 into a
  // different, valid-looking date.
  if (month < 1 || month > 12 || day < 1 || day > 31 || hour > 23 || minute > 59) return null;
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const probe = new Date(wall);
  // Catches what range-checking cannot: 31 April.
  if (probe.getUTCMonth() !== month - 1 || probe.getUTCDate() !== day) return null;

  try {
    let instant = wall;
    for (let pass = 0; pass < 2; pass += 1) {
      instant = wall - (wallAsUtcMs(new Date(instant), timeZone) - instant);
    }
    const solved = wallAsUtcMs(new Date(instant), timeZone);
    if (solved !== wall) instant += wall - solved;
    const result = new Date(instant);
    return Number.isNaN(result.getTime()) ? null : result.toISOString();
  } catch {
    // An unknown zone name. Refuse rather than guess one.
    return null;
  }
}

/** An instant as the `datetime-local` value that shows it in `timeZone`. */
export function instantToZoneInput(iso: string | null, timeZone: string): string {
  if (!iso) return "";
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "";
  try {
    return new Date(wallAsUtcMs(instant, timeZone)).toISOString().slice(0, 16);
  } catch {
    return "";
  }
}

const labelFormatters = new Map<string, Intl.DateTimeFormat>();

/**
 * "Tue, Oct 6, 9:30 PM CDT". The zone is spelled out because this is the string
 * an admin reads back before arming, and it is the real defence against an AM/PM
 * or wrong-zone slip.
 */
export function formatSiteMaintenanceTime(iso: string | null, timeZone: string): string {
  if (!iso) return "";
  const instant = new Date(iso);
  if (Number.isNaN(instant.getTime())) return "";
  try {
    let formatter = labelFormatters.get(timeZone);
    if (!formatter) {
      formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
        timeZoneName: "short",
      });
      labelFormatters.set(timeZone, formatter);
    }
    return formatter.format(instant);
  } catch {
    return instant.toISOString();
  }
}

/**
 * A coarse countdown. It stops counting seconds under a minute on purpose: a
 * number ticking every second beside a form somebody is filling in is hostile,
 * and the precision changes nothing they would do.
 */
export function formatSiteMaintenanceCountdown(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return "now";
  const totalMinutes = Math.floor(ms / 60_000);
  if (totalMinutes < 1) return "less than a minute";
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

/**
 * Milliseconds until the window starts, corrected for the viewer's clock. One
 * skew offset from the server's clock, applied to every tick: a laptop an hour
 * out otherwise shows a countdown that is confidently and uselessly wrong.
 */
export function msUntilSiteMaintenance(
  status: Pick<SiteMaintenanceStatus, "startsAt" | "serverTime"> | null | undefined,
  clientNow: number,
  receivedAt: number = clientNow,
): number | null {
  if (!status?.startsAt) return null;
  const startsAt = new Date(status.startsAt).getTime();
  const serverNow = new Date(status.serverTime).getTime();
  if (Number.isNaN(startsAt) || Number.isNaN(serverNow)) return null;
  const skew = receivedAt - serverNow;
  return startsAt - (clientNow - skew);
}

/** The final warning fires this close to the start. */
export const SITE_MAINTENANCE_FINAL_WARNING_MS = 5 * 60 * 1000;

/**
 * Whether to show the announcement popup: at most twice per announcement, once
 * when the person first sees it and once five minutes out. More often than that
 * and people learn to dismiss it unread, which makes an announcement worthless.
 */
export function shouldShowSiteMaintenanceAnnouncement(
  status: Pick<SiteMaintenanceStatus, "state" | "announcementId"> | null | undefined,
  untilStartMs: number | null,
  acknowledged: string | null,
  acknowledgedFinal: string | null,
): boolean {
  if (!status || status.state !== "scheduled" || !status.announcementId) return false;
  if (status.announcementId !== acknowledged) return true;
  return (
    untilStartMs !== null &&
    untilStartMs > 0 &&
    untilStartMs <= SITE_MAINTENANCE_FINAL_WARNING_MS &&
    status.announcementId !== acknowledgedFinal
  );
}

/** Which acknowledgement a popup on screen now belongs to. */
export function siteMaintenanceAnnouncementSlot(untilStartMs: number | null): "initial" | "final" {
  return untilStartMs !== null && untilStartMs <= SITE_MAINTENANCE_FINAL_WARNING_MS
    ? "final"
    : "initial";
}

/**
 * How long until the browser asks again, with jitter.
 *
 * The jitter is not decoration: without it every browser that loaded at the same
 * moment polls in lockstep for ever, a self-inflicted thundering herd against the
 * application you are trying to keep quiet.
 */
export function nextSiteMaintenancePollMs(
  state: SiteMaintenanceStateName | null | undefined,
  untilStartMs: number | null,
  random: () => number = Math.random,
): number {
  let base: number;
  if (state === "active") base = 15_000;
  else if (state === "scheduled") {
    base =
      untilStartMs !== null && untilStartMs <= SITE_MAINTENANCE_FINAL_WARNING_MS ? 10_000 : 30_000;
  } else base = 120_000;
  const jitter = 1 + (random() * 0.4 - 0.2); // ±20%
  return Math.round(base * jitter);
}

/* ── request bodies ─────────────────────────────────────────────────── */

/**
 * `PUT /api/maintenance`: arm, edit, or start a window now.
 *
 * One body for all three because they are one row. `forceActive` true is "Start
 * now"; an edit to a window that has already started sends it back as it was.
 * `expectedVersion` is the row's version as the screen loaded it; a stale one is
 * a 409, never a silent overwrite of another admin's change.
 */
export const setSiteMaintenanceBody = strictObject({
  startsAt: z.string().datetime({ offset: true }),
  expectedEndAt: z.string().datetime({ offset: true }).nullable(),
  message: z.string().trim().max(SITE_MAINTENANCE_MESSAGE_MAX),
  autoEnd: z.boolean(),
  forceActive: z.boolean(),
  expectedVersion: z.number().int().min(1),
}).superRefine((value, ctx) => {
  if (
    value.expectedEndAt !== null &&
    new Date(value.expectedEndAt).getTime() <= new Date(value.startsAt).getTime()
  ) {
    ctx.addIssue({
      code: "custom",
      path: ["expectedEndAt"],
      message: "the expected end must be after the start",
    });
  }
  if (value.autoEnd && value.expectedEndAt === null) {
    ctx.addIssue({
      code: "custom",
      path: ["autoEnd"],
      message: "ending automatically needs an expected end time",
    });
  }
});
export type SetSiteMaintenanceBody = z.infer<typeof setSiteMaintenanceBody>;

/** `POST /api/maintenance/end`. */
export const endSiteMaintenanceBody = strictObject({
  expectedVersion: z.number().int().min(1),
});
export type EndSiteMaintenanceBody = z.infer<typeof endSiteMaintenanceBody>;
